import React, {Fragment, useState, useEffect, useRef} from "react";
import { TimePicker, TimePrecision } from "@blueprintjs/datetime";
import { H3 } from "@blueprintjs/core";
import styled from "styled-components";
import moment from 'moment-timezone';
import * as countryCodes from "country-codes-list";
import * as currencyCodes from "currency-codes";

const AvailableCountryCodes = Object.keys(countryCodes.customList('countryCode'))
const AvailableCurrencyCodes = currencyCodes.codes()
const AvailableTimezones = [
    ...moment.tz.names().filter(name => name.includes('/') && !name.includes('Etc/')),
    // Backward compatibility for old recordings
    'PST8PDT',
    'CST6CDT',
    'EST5EDT',
    'MST7MDT',
]

const OpeningTimesRow = styled.div`
    display: flex;
    margin-bottom: 0.5em;
`

const toInternal = (values) => values.map(value => ({
    day: value.day,
    start: new Date(1987, 3, 25, value.startHour, value.startMinute),
    stop: new Date(1987, 3, 25, value.stopHour, value.stopMinute)
}));

const toApi = (values) => values.map(value => ({
    day: value.day,
    startHour: value.start.getHours(),
    startMinute: value.start.getMinutes(),
    stopHour: value.stop.getHours(),
    stopMinute: value.stop.getMinutes(),
}));

const hasErrors = (values) => values.reduce((acc, cur) => acc || cur.stop.getTime() <= cur.start.getTime(), false);

const OpeningTimes = ({values: propValues, editing, onChange}) => {
    const [values, setValues] = useState(() => toInternal(propValues));
    const prevPropValues = useRef(propValues);

    // Sync from props when identity changes
    useEffect(() => {
        if (propValues !== prevPropValues.current) {
            prevPropValues.current = propValues;
            setValues(toInternal(propValues));
        }
    }, [propValues]);

    const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

    const handleChange = (index, field, newDate) => {
        const newValues = values.map((v, i) =>
            i === index ? {...v, [field]: newDate} : v
        );
        setValues(newValues);
        onChange(toApi(newValues), hasErrors(newValues));
    };

    return <Fragment>
        <H3>Opening Times</H3>
        {values.map((value, index) => {
            const error = value.stop.getTime() <= value.start.getTime();
            const color = !editing ? "rgb(150,150,150)" : error ? "rgb(255,0,0)" : "rgb(0,0,0)";
            return <OpeningTimesRow key={value.day}>
                    <span style={{
                        color: color,
                        display: "inline-block",
                        minWidth: "4ch",
                        marginRight: "1em",
                        verticalAlign: "middle",
                    }}>{`${days[value.day]}:`}</span>
                <TimePicker
                    key="startPicker"
                    precision={TimePrecision.MINUTE}
                    disabled={!editing}
                    value={value.start}
                    onChange={(start) => handleChange(index, 'start', start)}
                />
                <span style={{
                    color: color,
                    display: "inline-block",
                    marginLeft: "0.5em",
                    marginRight: "0.5em",
                    verticalAlign: "middle",
                }}>{"- to -"}</span>
                <TimePicker
                    key="stopPicker"
                    precision={TimePrecision.MINUTE}
                    disabled={!editing}
                    value={value.stop}
                    onChange={(stop) => handleChange(index, 'stop', stop)}
                />
            </OpeningTimesRow>
        })}
    </Fragment>
};

export {
    AvailableTimezones,
    AvailableCurrencyCodes,
    AvailableCountryCodes,
    OpeningTimes
};
